import Image from "next/image"
import { useRouter } from "next/router"
import { useState } from "react"
import {
    Autocomplete,
    Button,
    Container,
    Dialog,
    Stack,
    Typography,
} from "@octopus-energy/coral/atoms"
import { useTranslations } from "next-intl"
import { getQuerylessPath } from "@/lib/routes"
import { nestedQueryParams } from "@/utils/nestedQueryParams"
import { type DartySession } from "../../session/darty"
import dartyStoresData from "./dartyStores.json"

type DartyStoreDialogProps = {
    isOpen: boolean
    onClose?: () => void | undefined
    session: DartySession
}

export function DartyStoreDialog({ isOpen, session, onClose }: DartyStoreDialogProps) {
    const t = useTranslations("DartyStoreDialog")
    const router = useRouter()

    const defaultValue = session.affiliateQueryParams?.store?.identifier

    const [store, setStore] = useState<string | null>(defaultValue ?? null)

    const dartyStores = dartyStoresData.map((store) => ({
        identifier: store.identifier,
        name: `Darty - ${store.name} (${store.identifier})`,
    }))

    const autocompleteOptions = dartyStores.map(({ identifier, name }) => ({
        value: identifier,
        label: name,
    }))

    const handleStoreChange = (value: string | null) => {
        setStore(value)
    }

    const handleStoreSubmit = async () => {
        const dartyStore = dartyStores.find((s) => s.identifier === store)

        const params = {
            affiliateQueryParams: { store: dartyStore, employee: { id: session.user.uid } },
        }
        const queryParams = nestedQueryParams.stringify(params)
        const currentPath = getQuerylessPath(router.asPath)

        await router.push(`${currentPath}?${queryParams}`)

        return router.reload()
    }

    return (
        <Dialog
            open={isOpen}
            onClose={onClose}
            ariaLabelledBy={t("title")}
            dialogWidth="sm"
        >
            <Container
                paddingX="md"
                height="breakpoint-md"
            >
                <Stack
                    gap="lg"
                    fullHeight
                    alignItems="center"
                    justifyContent="center"
                    flexDirection="column"
                >
                    <Typography textStyle="h3">{t("title")}</Typography>

                    <Image
                        width={336}
                        height={165}
                        alt={t("imageAlt")}
                        src="/assets/images/illustrations/constantine-astronaut.svg"
                    />

                    <Autocomplete
                        label={t("form.label")}
                        defaultValue={defaultValue}
                        options={autocompleteOptions}
                        onChange={(_label, value) => handleStoreChange(value)}
                        fullWidth
                    />

                    <Button
                        disabled={!store}
                        onClick={handleStoreSubmit}
                    >
                        {t("form.submit")}
                    </Button>
                </Stack>
            </Container>
        </Dialog>
    )
}

DartyStoreDialog.messages = ["DartyStoreDialog"] as const
